import type {
  EligibleTour,
  TourDefinition,
  TourStep,
  TourStepAction,
} from '@/features/feature-tours/types';
import { tourDefinitionWireSchema } from '../schemas/feature-tour';
import type {
  EligibleTourWire,
  TourStepActionWire,
  TourStepWire,
} from '../schemas/feature-tour';

/**
 * Convert a tour step action from wire format to domain format.
 * Maps snake_case to camelCase; navigate_to becomes an optional navigateTo.
 * The wire behavior is a string narrowed to the domain union.
 */
export function mapTourStepAction(wire: TourStepActionWire): TourStepAction {
  return {
    label: wire.label,
    behavior: wire.behavior as TourStepAction['behavior'],
    ...(wire.navigate_to !== undefined ? { navigateTo: wire.navigate_to } : {}),
  };
}

/**
 * Convert a tour step from wire format to domain format.
 * Maps snake_case to camelCase; an absent secondary_action stays undefined.
 */
export function mapTourStep(wire: TourStepWire): TourStep {
  return {
    stepIndex: wire.step_index,
    title: wire.title,
    body: wire.body,
    targetSelector: wire.target_selector,
    spotlight: wire.spotlight,
    primaryAction: mapTourStepAction(wire.primary_action),
    ...(wire.secondary_action !== undefined
      ? { secondaryAction: mapTourStepAction(wire.secondary_action) }
      : {}),
  };
}

/**
 * Parse a tour definition JSON string into a domain TourDefinition.
 * Resilient by design: malformed JSON or a schema-invalid shape yields an
 * empty definition ({ steps: [] }) rather than throwing.
 */
export function parseTourDefinition(definitionJson: string): TourDefinition {
  try {
    const raw = JSON.parse(definitionJson);
    const parsed = tourDefinitionWireSchema.parse(raw);
    return { steps: parsed.steps.map(mapTourStep) };
  } catch {
    return { steps: [] };
  }
}

/**
 * Convert an eligible tour from wire format to domain format.
 * Maps snake_case to camelCase and parses the definition JSON string.
 */
export function mapEligibleTour(wire: EligibleTourWire): EligibleTour {
  return {
    slug: wire.slug,
    version: wire.version,
    title: wire.title,
    description: wire.description,
    triggerType: wire.trigger_type,
    contextRoute: wire.context_route,
    priority: wire.priority,
    definition: parseTourDefinition(wire.definition),
  };
}
